import * as React from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { PageTitle } from '@/components/PageTitle'
import { useAuth } from '@/contexts/AuthContext'
import {
  getPortalImpersonationErrorMessage,
  usePortalImpersonation,
} from '@/hooks/use-cases/usePortalImpersonation'

export const Route = createFileRoute(
  '/_app/clients/$clientId/portal/impersonate',
)({ component: PortalImpersonateRoute })

function PortalImpersonateRoute() {
  const { clientId } = Route.useParams()
  const { isAuthenticated, isLoading } = useAuth()
  const { mutate, isError, error } = usePortalImpersonation(
    clientId,
    undefined,
    { sameTab: true },
  )
  const started = React.useRef(false)

  const ready = isAuthenticated && !isLoading

  React.useEffect(() => {
    if (!ready || started.current) return
    started.current = true
    mutate()
  }, [ready, mutate])

  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <PageTitle title="Opening client portal" />
      {isError ? (
        <div
          role="alert"
          className="flex max-w-sm flex-col items-center gap-3 text-center"
        >
          <AlertCircle className="size-10 text-destructive" aria-hidden />
          <h1 className="text-lg font-semibold">
            Couldn&apos;t open the client portal
          </h1>
          <p className="text-sm text-muted-foreground">
            {getPortalImpersonationErrorMessage(error)}
          </p>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
            <Button className="min-h-11" onClick={() => mutate()}>
              Try again
            </Button>
            <Button variant="outline" className="min-h-11" asChild>
              <Link to="/clients/$clientId" params={{ clientId }}>
                Back to client
              </Link>
            </Button>
          </div>
        </div>
      ) : (
        <div role="status" className="flex flex-col items-center gap-4">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">
            Opening client portal…
          </p>
        </div>
      )}
    </div>
  )
}
